import React from "react";
import type { NoteName } from "../../lib/guitarEngine";
import type { PianoFingeredKey } from "../../lib/pianoFingeringEngine";
import { degreeFunctionName, degreeRoman } from "../../lib/scaleDegreeLabels";
import { degreeColor } from "../../lib/practiceVisualTheme";

export interface ScaleDegreeChip {
  degree: string;
  note: NoteName;
}

export interface ScalePianoInspectorProps {
  rootNote: NoteName;
  scaleTitle: string;
  scaleInfo?: string;
  degreeChips: ScaleDegreeChip[];
  focusedDegree: string | null;
  selectedNoteName?: NoteName | null;
  onDegreeSelect: (degree: string) => void;
  fingering?: PianoFingeredKey[] | null;
}

export const ScalePianoInspector: React.FC<ScalePianoInspectorProps> = ({
  rootNote,
  scaleTitle,
  scaleInfo,
  degreeChips,
  focusedDegree,
  selectedNoteName,
  onDegreeSelect,
  fingering,
}) => {
  return (
    <div className="space-y-3">
      <section
        className="p-4 rounded-2xl bg-white/[0.07] border border-white/12"
        style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.12)" }}
      >
        <p className="text-[10px] font-sans font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">
          Qué estás aprendiendo
        </p>
        <h2 className="text-2xl font-serif font-bold italic text-slate-100 leading-tight">
          {rootNote} {scaleTitle}
        </h2>
        {scaleInfo && (
          <p className="mt-2 text-[12px] font-sans text-slate-400 leading-relaxed">{scaleInfo}</p>
        )}

        {focusedDegree && selectedNoteName && (
          <div
            className="mt-3 rounded-xl px-3 py-2 border"
            style={{
              backgroundColor: `${degreeColor(focusedDegree.replace("*", ""))}18`,
              borderColor: `${degreeColor(focusedDegree.replace("*", ""))}40`,
            }}
          >
            <p className="text-[9px] font-sans uppercase tracking-wider text-slate-500 mb-0.5">
              Ahora miras
            </p>
            <p className="text-sm font-sans font-semibold text-slate-100">
              <span className="font-serif italic">{selectedNoteName}</span>
              <span className="text-slate-400 font-normal mx-1">·</span>
              <span style={{ color: degreeColor(focusedDegree.replace("*", "")) }}>
                {degreeRoman(focusedDegree)}
              </span>
              <span className="text-slate-500 font-normal text-xs ml-1">
                ({degreeFunctionName(focusedDegree)})
              </span>
            </p>
          </div>
        )}

        <p className="mt-3 text-[11px] font-sans text-slate-400 leading-snug">
          Los colores del pentagrama y del teclado coinciden. Toca un grado aquí o una nota en la
          partitura para escucharla y verla resaltada.
        </p>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {degreeChips.map(({ degree, note }) => {
            const active = focusedDegree === degree;
            const color = degreeColor(degree.replace("*", ""));
            return (
              <button
                key={`${degree}-${note}`}
                type="button"
                onClick={() => onDegreeSelect(degree)}
                className={`group flex flex-col items-center min-w-[2.85rem] px-2 py-1.5 rounded-xl border transition-all duration-150 ${
                  active
                    ? "bg-white/10 shadow-sm scale-[1.03]"
                    : "bg-white/[0.04] border-white/10 hover:bg-white/[0.08] hover:border-white/15"
                }`}
                style={
                  active
                    ? { borderColor: `${color}66`, boxShadow: `0 0 0 1px ${color}33` }
                    : undefined
                }
                title={`${degreeFunctionName(degree)} — ${note}`}
              >
                <span
                  className="text-[12px] font-serif font-semibold leading-none tracking-wide"
                  style={{ color: active ? color : "rgba(248,250,252,0.92)" }}
                >
                  {degreeRoman(degree)}
                </span>
                <span
                  className={`text-[10px] font-sans mt-1 transition-colors ${
                    active ? "text-slate-300" : "text-slate-500 group-hover:text-slate-400"
                  }`}
                >
                  {note}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {fingering && fingering.some((k) => k.finger) && (
        <section
          className="p-4 rounded-2xl bg-white/[0.06] border border-white/10"
          style={{ boxShadow: "0 4px 20px rgba(45,42,38,0.04)" }}
        >
          <h4 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 font-sans mb-2">
            Dedos en el teclado
          </h4>
          <div className="space-y-1 max-h-32 overflow-y-auto scrollbar-thin">
            {fingering
              .filter((k) => k.finger)
              .slice(0, 10)
              .map((k) => (
                <div
                  key={k.midi}
                  className="flex items-center gap-2 text-[11px] font-sans text-slate-400"
                >
                  <span className="w-5 h-5 rounded-full bg-slate-800/90 text-white text-[10px] flex items-center justify-center font-bold shrink-0">
                    {k.finger}
                  </span>
                  <span className="text-slate-200">{k.step}</span>
                  <span className="text-slate-500">· {degreeRoman(k.degree)}</span>
                  <span className="text-slate-600 ml-auto text-[10px]">
                    {k.hand === "L" ? "izq." : "der."}
                  </span>
                </div>
              ))}
          </div>
        </section>
      )}
    </div>
  );
};
